import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkAuth } from './store/slices/authSlice';

import LoginPage from './pages/LoginPage';
import TenantPickerPage from './pages/TenantPickerPage';
import DashboardPage from './pages/DashboardPage';
import JobsPage from './pages/JobsPage';
import PipelinesTab from './components/analytics/PipelinesTab';
import AdminPage from './pages/AdminPage';
import APIKeysPage from './pages/APIKeysPage';
import GodModePage from './pages/GodModePage';
import AnalyticsPage from './pages/AnalyticsPage';
import DataSourcesPage from './pages/DataSourcesPage';
import DataOperationsPage from './pages/DataOperationsPage';
import DataSciencePage from './pages/DataSciencePage';
import SpreadsheetPage from './pages/SpreadsheetPage';
import IdentitiesPage from './pages/IdentitiesPage';
import MyTenantsPage from './pages/MyTenantsPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import SyncProgressPanel from './components/SyncProgressPanel';
import TopicProgressPanel from './components/TopicProgressPanel';
import { OnboardingProvider, OnboardingManager } from './components/onboarding';

// Lazy load DebugBar for god mode users only
const DebugBar = lazy(() => import('./components/debug/DebugBar'));

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, user } = useSelector((state) => state.auth);

  useEffect(() => {
    // Check auth status on app load
    dispatch(checkAuth());
  }, [dispatch]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  // Wrap authenticated content with OnboardingProvider
  const AuthenticatedContent = () => (
    <OnboardingProvider>
      <Routes>
        <Route path="/tenant-picker" element={<TenantPickerPage />} />
        <Route path="/analytics/views/:viewId/spreadsheet" element={<SpreadsheetPage />} />
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/pipelines" element={<PipelinesTab />} />
          <Route path="/data-sources/*" element={<DataSourcesPage />} />
          <Route path="/analytics/*" element={<AnalyticsPage />} />
          <Route path="/data-operations/*" element={<DataOperationsPage />} />
          <Route path="/data-science/*" element={<DataSciencePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/api-keys" element={<APIKeysPage />} />
          <Route path="/god-mode" element={<GodModePage />} />
          <Route path="/identities" element={<IdentitiesPage />} />
          <Route path="/my-tenants" element={<MyTenantsPage />} />
          {/* Redirect old settings URL */}
          <Route path="/settings" element={<Navigate to="/identities" replace />} />
        </Route>
      </Routes>

      {/* Onboarding Manager - shows tutorial modals when needed */}
      <OnboardingManager />

      {/* Sync Progress Panel - shows when a sync workflow is running */}
      <SyncProgressPanel />

      {/* Topic Progress Panel - shows when clustering or embedding is running */}
      <TopicProgressPanel />

      {/* Debug Bar for god mode users (only in staging) */}
      {user?.isGodMode && import.meta.env.VITE_ENABLE_DEBUG_BAR === 'true' && (
        <Suspense fallback={null}>
          <DebugBar />
        </Suspense>
      )}
    </OnboardingProvider>
  );

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/*" element={<AuthenticatedContent />} />
      </Route>

      {/* Default redirect */}
      <Route
        path="/"
        element={
          isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />
        }
      />
    </Routes>
  );
}

export default App;
